<template>
  <div
    class="relative dark:text-dark-50 text-light-950"
    :style="{
      color: theme ? theme.base.text.base : '',
      transformStyle: 'preserve-3d',
      backfaceVisibility: 'hidden'
    }"
  >
    <div
      class="relative z-20 h-full overflow-hidden"
      :class="[
        type === 'post' ? 'bg-light-100 dark:bg-dark-800 rounded-3xl' : '',
        type === 'comment' ? 'bg-light-100 dark:bg-dark-800 rounded-2xl' : '',
        type === 'comment-reply' ? 'bg-light-200 dark:bg-dark-700 rounded-xl' : '',
      ]"
      ref="$container"
    >
      <div class="flex flex-col h-full">
        <div class="p-3 shrink-0" v-if="type === 'post-main' && post.parent">
          <NuxtLink :to="{ query: { ...route.query, post: post.parent } }" class="bg-light-200 dark:bg-dark-800 py-3 px-3.5 rounded-xl block" v-if="post['@parent']">
            <p class="font-bold text-2xs">
              {{ $t('components.Post.answer.title') }}
            </p>
            <div class="line-clamp-2">{{ stripTags(post['@parent'].content) }}</div>
          </NuxtLink>
          <SkeletonBase class="h-24 rounded-xl !bg-light-200 dark:!bg-dark-800" is-animated v-else-if="post.parent" />
        </div>

        <PostBlock
          :post="post"
          :parent="parent"
          :type="type"
          class="grow"
          :is-visible="isVisible"
          :size="size"
          :is-votes="isVotes"
          :line-clamp="size === 'sm' ? 1 : 3"
          :is-hide-cheers="size === 'sm'"
          :is-hide-user="isHideUser"
          :theme="theme"
          :is-hide-actions="isHideActions"
          :can-reply="!parent?.parent"
          @scroll-start="() => isSlideBlocked = true"
          @scroll-end="() => isSlideBlocked = false"
          @answer="onAnswer"
        />

        <div class="pb-1.5 px-3 space-y-3 shrink-0" v-if="type === 'post' && isPreviewComments && size !== 'sm'">
          <hr class="border-t border-light-300 dark:border-dark-600" v-if="(post?.posts ?? []).length > 0" />

          <template v-if="size !== 'sm'">
            <NuxtLink :to="{ query: { ...route.query, post: post._id, isComments: 'true' } }" class="space-y-3 block" v-if="(post?.posts ?? []).length > 0">
              <PostBlockTiny
                :post="comment"
                :theme="theme"
                v-for="(comment, i) in post['@posts'].sort((a, b) => b.popularity - a.popularity).slice(0, 1)"
              />
            </NuxtLink>
          </template>

          <div class="flex items-center bg-light-200 text-light-700 dark:bg-dark-900 dark:text-dark-400 rounded-full p-2 -mx-1.5 text-sm space-x-2 cursor-pointer" @click="() => isCommenting = true">
            <UserPicture :user="currentUser" size="2xs" is-hide-badges class="shrink-0" />

            <div class="font-medium">
              {{ $t('components.Post.comments.action') }}
            </div>
          </div>
        </div>
      </div>

      <div class="space-x-3 flex items-center px-3 mt-4 mb-2" v-if="type === 'post-main' && isDisplayComments && post.posts.length > 0">
        <p class="font-bold text-sm shrink-0">{{ $t('components.Post.answers') }}</p>
        <hr class="border-t border-light-300 grow dark:border-dark-600" />
      </div>

      <CommentFeed
        :post="post"
        class="px-3 pb-32"
        :comments="post['@posts'] ?? []"
        @answer="(postId, isComment) => onAnswer(postId, isComment)"
        ref="$comments"
        v-if="type === 'post-main' && isDisplayComments && post.posts.length > 0"
      />
      <div
        class="flex grow"
        :class="[
          type === 'comment' ? 'px-3 pt-0 pb-3' : ''
        ]"
        v-else-if="isDisplayComments && post.posts.length > 0"
      >
        <div class="w-0.5 bg-light-200 dark:bg-dark-700 mr-3 shrink-0" v-if="type === 'comment'" :class="[ type === 'comment' ? '' : '']" />

        <div
          class="grow"
          :class="[
            type === 'comment' ? 'space-y-2' : 'space-y-2 px-3 pt- pb-24'
          ]"
        >
          <div class="relative" v-for="(comment, i) in (post['@posts'] ?? [])">
            <div class="h-full flex-col items-end flex absolute top-0 left-0 -translate-x-full w-6" v-if="type === 'comment'">
              <div class="h-2.5" />
            
              <div class="size-[1.1rem] relative">
                <svg class="relative z-[1] dark:hidden" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="1 2 10 11">
                  <path fill="#ede5eb" fill-rule="evenodd" d="M9.877 12H11.5a.5.5 0 0 0 0-1H9.9c-1.128 0-1.945 0-2.586-.053-.637-.052-1.057-.152-1.403-.329a3.5 3.5 0 0 1-1.53-1.529c-.176-.346-.276-.766-.328-1.403C4 7.045 4 6.228 4 5.1V3.5a.5.5 0 0 0-1 0v1.623c0 1.1 0 1.958.056 2.645.057.698.175 1.265.434 1.775a4.5 4.5 0 0 0 1.967 1.966c.51.26 1.077.378 1.775.435C7.92 12 8.776 12 9.877 12Z" clip-rule="evenodd"/>
                </svg>
                <svg class="relative z-[1] hidden dark:block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="1 2 10 11">
                  <path fill="#2d2c2c" fill-rule="evenodd" d="M9.877 12H11.5a.5.5 0 0 0 0-1H9.9c-1.128 0-1.945 0-2.586-.053-.637-.052-1.057-.152-1.403-.329a3.5 3.5 0 0 1-1.53-1.529c-.176-.346-.276-.766-.328-1.403C4 7.045 4 6.228 4 5.1V3.5a.5.5 0 0 0-1 0v1.623c0 1.1 0 1.958.056 2.645.057.698.175 1.265.434 1.775a4.5 4.5 0 0 0 1.967 1.966c.51.26 1.077.378 1.775.435C7.92 12 8.776 12 9.877 12Z" clip-rule="evenodd"/>
                </svg>

                <div class="absolute -bottom-0.5 left-0.5 size-4 bg-light-100 dark:bg-dark-800 rounded" />
              </div>

              <div class="grow w-full" :class="[ i >= post['@posts'].length - 1 && type !== 'comment' ? 'bg-light-100 dark:bg-dark-800' : '' ]"  />
            </div>

            <PostContainer
              :post="comment"
              :type="type === 'comment' ? 'comment-reply' : 'comment'"
              :parent="post"
              :is-display-comments="type !== 'comment'"
              :can-reply="!parent?.parent"
              :theme="theme"
              @answer="onAnswer"
              :key="comment._id"
            />
          </div>

          <div class="relative" v-if="type === 'comment'">
            <div class="h-full flex-col items-end flex absolute top-0 left-0 -translate-x-full w-6">          
              <div class="size-[1.1rem] relative">
                <svg class="relative z-[1] dark:hidden" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="1 2 10 11">
                  <path fill="#ede5eb" fill-rule="evenodd" d="M9.877 12H11.5a.5.5 0 0 0 0-1H9.9c-1.128 0-1.945 0-2.586-.053-.637-.052-1.057-.152-1.403-.329a3.5 3.5 0 0 1-1.53-1.529c-.176-.346-.276-.766-.328-1.403C4 7.045 4 6.228 4 5.1V3.5a.5.5 0 0 0-1 0v1.623c0 1.1 0 1.958.056 2.645.057.698.175 1.265.434 1.775a4.5 4.5 0 0 0 1.967 1.966c.51.26 1.077.378 1.775.435C7.92 12 8.776 12 9.877 12Z" clip-rule="evenodd"/>
                </svg>
                <svg class="relative z-[1] hidden dark:block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="1 2 10 11">
                  <path fill="#2d2c2c" fill-rule="evenodd" d="M9.877 12H11.5a.5.5 0 0 0 0-1H9.9c-1.128 0-1.945 0-2.586-.053-.637-.052-1.057-.152-1.403-.329a3.5 3.5 0 0 1-1.53-1.529c-.176-.346-.276-.766-.328-1.403C4 7.045 4 6.228 4 5.1V3.5a.5.5 0 0 0-1 0v1.623c0 1.1 0 1.958.056 2.645.057.698.175 1.265.434 1.775a4.5 4.5 0 0 0 1.967 1.966c.51.26 1.077.378 1.775.435C7.92 12 8.776 12 9.877 12Z" clip-rule="evenodd"/>
                </svg>

                <div class="absolute -bottom-0.5 left-0.5 size-4 bg-light-100 dark:bg-dark-800 rounded" />
              </div>  

              <div class="grow w-full bg-light-100 dark:bg-dark-800" />
            </div>

            <ButtonBase color="white" size="2xs" is-full class="shrink-0" @click="() => emit('answer', post._id, false)">
              <div class="flex items-center space-x-1.5 text-xs">
                <div class="ml-1">{{ $t('actions.reply') }}</div>
                <IconBase name="cute/comment" :width="18" />
              </div>
            </ButtonBase>
          </div>
        </div>
      </div>
    </div>

    <PopinBase :is-active="isCommenting" size="2xs" is-outer-close @close="() => isCommenting = false">
      <template #body>
        <div>
          <PostSubmit :post="post" ref="$comment" @end="() => isCommenting = false" />
        </div>
      </template>
    </PopinBase>
  </div>
</template>

<script lang="ts" setup>
import { Post, Theme } from '@/classes'
import UserStore from '@/store/user'
import SentimentsStore from '@/store/sentiments'

const props = withDefaults(defineProps<{
  post: Post
  parent?: Post | null
  size?: 'base' | 'sm'
  type?: 'post' | 'post-main' | 'comment' | 'comment-reply'
  isDisplayComments?: boolean
  isPreviewComments?: boolean
  isSwipeTutorial?: boolean
  isVotes?: boolean
  isHideUser?: boolean
  isHideActions?: boolean
  scrollRef?: HTMLElement | 'window'
  theme?: Theme | null
}>(), {
  parent: null,
  type: 'post',
  theme: null,
  size: 'base',
  isSwipeTutorial: false,
  scrollRef: 'window',
  isVotes: false,
  isHideUser: false,
  isHideActions: false,
  isPreviewComments: true,
  isDisplayComments: false
})

const emit = defineEmits<{
  (e: 'answer', postId: string, isComment: boolean): void
  (e: 'size', size: { width: number, height: number }): void
  (e: 'visible'): void
}>()

const { watchersAdd, watchersRestart, watchersStop } = useWatchers()

useKeepAlive({
  onMounted: () => {
    setTimeout(() => minHeight.value = $container.value?.clientHeight ?? 0, 100)
  },
  onActivated: () => {
    watchersRestart()
  },
  onLeave: () => {
    watchersStop()
  }
})


const { post } = toRefs(props)
const route = useRoute()
const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)
const sentimentsStore = SentimentsStore()

const minHeight = ref(0)

const onAnswer = async (postId: string, isComment: boolean) => {
  emit('answer', postId, isComment)
}

const $container = ref()
const isSlideBlocked = ref(false)
const isVisible = ref(false)
const isSkipped = ref(false)

const { stop } = useIntersectionObserver($container,
  ([entry]) => {    
    if (entry?.isIntersecting) {
      sentimentsStore.skip({
        postId: props.post._id
      })
      
      isSkipped.value = true

      stop()
    }
  },
  {
    threshold: 0.8
  }
)

useIntersectionObserver($container,
  ([entry]) => {
    isVisible.value = entry?.isIntersecting ? true : false
  },
  {
    threshold: 0.01
  }
)

const $comment = ref()
const isCommenting = ref(false)

watchersAdd(() => watch(isCommenting, (v) => {
  if (v) {
    setTimeout(() => $comment.value?.focus())
  }
}))

const $comments = ref()

defineExpose({
  next: () => {
    $comments.value?.next()
  },
  position: () => {
    if (!$container.value) return 0
    
    const { top } = $container.value.getBoundingClientRect()
    return top
  }
})
</script>
